import { useMutation } from '@tanstack/react-query';
import { KeyRound } from 'lucide-react';
import { FormattedMessage } from 'react-intl';

import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { generateJwtToken } from '@/lib/queries/mcpJwt';
import { type Project } from '@/lib/queries/project';
import { cn } from '@/lib/utils';

interface Props {
  mainProject: Project;
}

export const GenerateJwtButton = ({ mainProject }: Props): JSX.Element => {
  const { toast } = useToast();

  const { isPending, mutateAsync } = useMutation({
    mutationKey: ['user', mainProject.id, 'generate-jwt'],
    mutationFn: async () => generateJwtToken(mainProject.id),
    onSuccess: token => {
      navigator.clipboard.writeText(token);
      toast({
        title: 'configuration.mcpJwt.button.success',
        variant: 'default',
      });
    },
    onError: () => {
      toast({
        title: 'configuration.mcpJwt.button.error',
        variant: 'destructive',
      });
    },
  });

  return (
    <section>
      <h1 className="text-xl mb-2">
        <FormattedMessage id="configuration.mcpJwt.title" />
      </h1>
      <Button
        variant="main"
        className="text-white"
        onClick={() => mutateAsync()}
        disabled={isPending}
      >
        <KeyRound
          size={24}
          className={cn('inline mr-2', isPending ? 'animate-spin' : '')}
        />
        <span className="inline-block min-w-[100px] text-center">
          {isPending ? (
            <FormattedMessage id="configuration.mcpJwt.button.pending" />
          ) : (
            <FormattedMessage id="configuration.mcpJwt.button.label" />
          )}
        </span>
      </Button>
    </section>
  );
};
